// DBOS Transact page interactions:
//  - BenefitItem/Content/Image: "Transact #1/#2/#3 - Open". Clicking a benefit expands it, collapses the
//    others, and cross-fades the matching screenshot.
//  - FeatureHighlight: "Feature Highlight [Scroll]". Cards shrink to 80% as they scroll past.
//  - TooltipTarget: the custom cursor-following tooltip on the supported-tools icons.

import { useEffect, useRef } from 'react';
import type { WfProps } from '../webflow/element';
import { css } from './easing';

// --- Benefit accordion -------------------------------------------------------------------------

type Part = 'item' | 'content' | 'image';

const CLOSED = 80;
// Expanded heights per item, from the IX2 actions (the "tiny" breakpoint has its own set).
const OPEN_DESKTOP = [180, 142, 118];
const OPEN_MOBILE = [260, 166, 166];
const BORDER_ACTIVE = 'rgb(50, 62, 87)';
const BORDER_IDLE = 'rgb(25, 31, 43)';

// The page has a single accordion, whose items, texts and screenshots live in different
// branches of the DOM, so its parts register here by index.
const parts: Record<Part, Array<HTMLElement | null>> = { item: [], content: [], image: [] };
let running: Animation[] = [];

function animate(el: HTMLElement | null | undefined, to: Keyframe, duration: number, easing: string) {
  if (!el?.animate) return;
  // A single keyframe animates from the element's current value.
  const anim = el.animate([to], { duration, easing, fill: 'forwards' });
  running.push(anim);
  anim.onfinish = () => {
    anim.commitStyles();
    anim.cancel();
  };
}

function openBenefit(i: number) {
  // Restarting the interaction keeps whatever state the previous run reached.
  for (const a of running) {
    if (a.playState !== 'finished') {
      a.commitStyles();
      a.cancel();
    }
  }
  running = [];
  const { item, content, image } = parts;
  const heights = window.matchMedia('(max-width: 479px)').matches ? OPEN_MOBILE : OPEN_DESKTOP;
  const outExpo = css('outExpo');
  // First collapse the clicked item and hide its text...
  animate(item[i], { height: `${CLOSED}px` }, 500, css('inOutExpo'));
  animate(content[i], { opacity: '0' }, 500, css('inOutExpo'));
  setTimeout(() => {
    // ...then open it and close the others.
    for (let j = 0; j < 3; j++) {
      const active = j === i;
      animate(item[j], { height: `${active ? heights[j] : CLOSED}px` }, 1000, outExpo);
      animate(image[j], { opacity: active ? '1' : '0' }, 1000, outExpo);
      animate(content[j], { opacity: active ? '1' : '0' }, 1000, outExpo);
      animate(
        item[j],
        { borderColor: active ? BORDER_ACTIVE : BORDER_IDLE },
        j === 2 ? 500 : 1000,
        j === 2 ? 'linear' : outExpo,
      );
    }
  }, 500);
}

const registerPart = (part: Part, index: number) => (el: HTMLElement | null) => {
  parts[part][index] = el;
};

export function BenefitItem({ as: Tag = 'div', index, children, ...props }: WfProps & { index: number }) {
  return (
    <Tag ref={registerPart('item', index)} {...props} onClick={() => openBenefit(index)}>
      {children}
    </Tag>
  );
}

export function BenefitContent({ as: Tag = 'div', index, children, ...props }: WfProps & { index: number }) {
  return (
    <Tag ref={registerPart('content', index)} {...props}>
      {children}
    </Tag>
  );
}

export function BenefitImage({ index, ...props }: React.ImgHTMLAttributes<HTMLImageElement> & { index: number }) {
  return <img ref={registerPart('image', index)} {...props} />;
}

// --- Feature highlight -------------------------------------------------------------------------

// Scale keyframes per card over the scroll progress (0..1) of the grid.
const CARD_KEYFRAMES: Array<[number, number, number, number]> = [
  // [start, end, from, to]
  [0, 0.25, 1, 0.8],
  [0.25, 0.5, 1, 0.8],
  [0.5, 0.75, 1, 0.8],
];

/** Webflow's "while scrolling in view" progress with default offsets. */
function scrollProgress(el: HTMLElement): number {
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const line = r.top + Math.min(r.height, vh);
  const span = Math.min(vh + (r.top + r.height - line), document.documentElement.scrollHeight);
  return Math.min(Math.max(0, vh - line), span) / span;
}

export function FeatureHighlight({ as: Tag = 'div', children, ...props }: WfProps) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const cards = [...root.querySelectorAll<HTMLElement>('.features_card')];
    let raf = 0;
    let shown = -1; // smoothed progress
    const render = () => {
      raf = 0;
      // Only on the "main" and "medium" breakpoints (tablet and up).
      if (window.innerWidth < 768) {
        cards.forEach((c) => (c.style.transform = ''));
        shown = -1;
        return;
      }
      const target = scrollProgress(root);
      // IX2 smoothing 50: close half the remaining distance each frame.
      shown = shown < 0 ? target : shown + (target - shown) * 0.5;
      if (Math.abs(target - shown) < 0.0005) shown = target;
      cards.forEach((card, i) => {
        const kf = CARD_KEYFRAMES[i];
        if (!kf) return;
        const [start, end, from, to] = kf;
        const t = Math.min(1, Math.max(0, (shown - start) / (end - start)));
        const s = from + (to - from) * t;
        card.style.willChange = 'transform';
        card.style.transform = `scale3d(${s}, ${s}, 1)`;
      });
      if (shown !== target) raf = requestAnimationFrame(render);
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(render);
    };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);
  return (
    <Tag ref={ref} {...props}>
      {children}
    </Tag>
  );
}

// --- Cursor tooltip ----------------------------------------------------------------------------

let tooltip: HTMLDivElement | null = null;

function getTooltip(): HTMLDivElement {
  if (!tooltip) {
    tooltip = document.createElement('div');
    tooltip.className = 'tooltip';
    tooltip.style.opacity = '0';
    tooltip.style.pointerEvents = 'none';
    tooltip.style.position = 'fixed';
    document.body.appendChild(tooltip);
  }
  return tooltip;
}

/** An element whose `tooltip-text` is shown in a pill that follows the cursor. */
export function TooltipTarget({ as: Tag = 'div', children, ...props }: WfProps & { 'tooltip-text': string }) {
  const text = props['tooltip-text'];
  return (
    <Tag
      {...props}
      onMouseEnter={() => {
        const t = getTooltip();
        t.textContent = text;
        t.style.opacity = '1';
        t.style.transform = 'translate(1.5rem, -25%)';
      }}
      onMouseMove={(e: React.MouseEvent) => {
        const t = getTooltip();
        t.style.left = `${e.clientX}px`;
        t.style.top = `${e.clientY}px`;
      }}
      onMouseLeave={() => {
        const t = getTooltip();
        t.style.opacity = '0';
        t.style.transform = 'translate(0%, 0%)';
      }}
    >
      {children}
    </Tag>
  );
}
